import type { LucideIcon, LucideProps } from "lucide-react";
import {
  Activity,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Bell,
  Calendar,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  Clock,
  Columns3,
  Copy,
  CreditCard,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  FileText,
  GraduationCap,
  History,
  Inbox,
  Info,
  LayoutDashboard,
  Link,
  ListFilter,
  Loader,
  Lock,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  MessageCircle,
  Minus,
  OctagonAlert,
  Pause,
  Pencil,
  Play,
  Plus,
  QrCode,
  Radio,
  RefreshCw,
  RotateCw,
  Search,
  Settings,
  ShieldCheck,
  Shuffle,
  Sigma,
  SlidersHorizontal,
  Smartphone,
  Target,
  Trash2,
  TriangleAlert,
  Unplug,
  UserMinus,
  UserPlus,
  Users,
  X,
  Zap,
} from "lucide-react";
import { cn } from "../lib/cn";

/**
 * Subconjunto Lucide do produto (65 ícones, os mesmos de /design-system/assets/icons).
 * Traço 2px, pontas redondas; nunca ícone preenchido, emoji ou glifo unicode.
 * Tamanhos: 16 em menu/botão, 13–14 em pill, 12 em badge, 20 em estado vazio.
 */
const ICON_MAP = {
  "activity": Activity,
  "arrow-down": ArrowDown,
  "arrow-right": ArrowRight,
  "arrow-up": ArrowUp,
  "bell": Bell,
  "calendar": Calendar,
  "chart-column": ChartColumn,
  "check": Check,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  "chevrons-up-down": ChevronsUpDown,
  "circle-alert": CircleAlert,
  "circle-check": CircleCheck,
  "circle-help": CircleHelp,
  "clock": Clock,
  "columns-3": Columns3,
  "copy": Copy,
  "credit-card": CreditCard,
  "download": Download,
  "ellipsis": Ellipsis,
  "external-link": ExternalLink,
  "eye": Eye,
  "file-text": FileText,
  "graduation-cap": GraduationCap,
  "history": History,
  "inbox": Inbox,
  "info": Info,
  "layout-dashboard": LayoutDashboard,
  "link": Link,
  "list-filter": ListFilter,
  "loader": Loader,
  "lock": Lock,
  "log-out": LogOut,
  "mail": Mail,
  "megaphone": Megaphone,
  "menu": Menu,
  "message-circle": MessageCircle,
  "minus": Minus,
  "octagon-alert": OctagonAlert,
  "pause": Pause,
  "pencil": Pencil,
  "play": Play,
  "plus": Plus,
  "qr-code": QrCode,
  "radio": Radio,
  "refresh-cw": RefreshCw,
  "rotate-cw": RotateCw,
  "search": Search,
  "settings": Settings,
  "shield-check": ShieldCheck,
  "shuffle": Shuffle,
  "sigma": Sigma,
  "sliders-horizontal": SlidersHorizontal,
  "smartphone": Smartphone,
  "target": Target,
  "trash-2": Trash2,
  "triangle-alert": TriangleAlert,
  "unplug": Unplug,
  "user-minus": UserMinus,
  "user-plus": UserPlus,
  "users": Users,
  "x": X,
  "zap": Zap,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICON_MAP;

/** Os nomes aceitos por `<Icon name>`, na ordem alfabética. */
export const ICON_NAMES = Object.keys(ICON_MAP) as IconName[];

export interface IconProps extends Omit<LucideProps, "ref"> {
  name: IconName;
  /** px. Padrão 16. */
  size?: number;
  /** Com rótulo vira imagem acessível; sem rótulo é decorativo (aria-hidden). */
  "aria-label"?: string;
}

export function Icon({ name, size = 16, strokeWidth = 2, className, ...props }: IconProps) {
  const Glyph = ICON_MAP[name];
  const labelled = Boolean(props["aria-label"]);
  return (
    <Glyph
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      aria-hidden={labelled ? undefined : true}
      role={labelled ? "img" : undefined}
      className={cn("shrink-0", className)}
      {...props}
    />
  );
}
